import type { DeviceStatus, DeviceType } from '@/modules/device/lib/device.schema';
import type { ParseKeys } from 'i18next';
import { Wifi, Radio, MonitorSmartphone, DoorOpen, HelpCircle } from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';

export type DeviceLabelKey = ParseKeys<'device'>;

// Full label lookup for every listed type. UNASSIGNED is absent on purpose — it is
// never a selectable type, and consumers fall back to the raw enum value for it.
const DEVICE_TYPE_LABELS: Partial<Record<DeviceType, DeviceLabelKey>> = {
  BLE_POSITIONING: 'deviceTypes.blePositioning',
  GATEWAY: 'deviceTypes.gateway',
  E_LABEL: 'deviceTypes.elabel',
  DOOR_SENSOR: 'deviceTypes.doorSensor',
};

// Enum listing narrowed by the active build profile via `__DEVICE_TYPES__`.
const ALL_DEVICE_TYPES = Object.keys(DEVICE_TYPE_LABELS) as DeviceType[];

export const DEVICE_TYPES: { value: DeviceType; labelKey: DeviceLabelKey }[] = (
  __DEVICE_TYPES__ ?? ALL_DEVICE_TYPES
)
  .map((value) => {
    const labelKey = DEVICE_TYPE_LABELS[value as DeviceType];
    return labelKey ? { value: value as DeviceType, labelKey } : null;
  })
  .filter((entry): entry is NonNullable<typeof entry> => entry !== null);

export const DEVICE_STATUSES: { value: DeviceStatus; labelKey: DeviceLabelKey }[] = [
  { value: 'active', labelKey: 'deviceStatuses.active' },
  { value: 'maintenance', labelKey: 'deviceStatuses.maintenance' },
  { value: 'decommissioned', labelKey: 'deviceStatuses.decommissioned' },
];

export const DEVICE_STATUS_COLOR: Record<
  DeviceStatus,
  'success' | 'warning' | 'danger' | 'neutral'
> = {
  unassigned: 'neutral',
  active: 'success',
  maintenance: 'warning',
  decommissioned: 'danger',
};

export const DEVICE_TYPE_ICON: Record<DeviceType, ComponentType<SVGProps<SVGSVGElement>>> = {
  UNASSIGNED: HelpCircle,
  BLE_POSITIONING: Radio,
  GATEWAY: Wifi,
  E_LABEL: MonitorSmartphone,
  DOOR_SENSOR: DoorOpen,
};

export const ALERT_STATUS_COLOR: Record<string, 'success' | 'warning' | 'danger'> = {
  unresolved: 'danger',
  snoozed: 'warning',
  normal: 'success',
};
